import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from '@/modules/auth/hooks';
import { useCashbackCustomer } from '@/modules/cashback/api';
import { landingService } from '@/modules/landing/api';
import {
  useAppointment,
  useAppointmentAction,
  useCreateAppointment,
  useUpdateAppointment,
} from '../../api';
import { useBookingMode } from '../../booking-mode';
import type { SlotChoice } from '../professional-slot-picker';

export type WizardStep = 'customer' | 'service' | 'professional' | 'summary';

export const WIZARD_STEPS: WizardStep[] = ['customer', 'service', 'professional', 'summary'];

export const WIZARD_LABELS: Record<WizardStep, string> = {
  customer: 'Cliente',
  service: 'Servico',
  professional: 'Profissional e horario',
  summary: 'Resumo',
};

export interface WizardCustomer {
  id: string;
  name: string;
  phone: string | null;
  /** Foto do cadastro; sem ela o resumo cai nas iniciais. */
  image?: string | null;
  /** Prova de posse do WhatsApp; so existe no agendamento publico. */
  token?: string;
}

export interface WizardService {
  serviceId: string;
  name: string;
  price: number;
  durationMinutes: number;
  maxDiscountPercent: number;
  /** Primeira foto do servico; sem ela entra o icone da comanda. */
  image?: string | null;
}

export interface WizardRow extends WizardService {
  /** Chave local: o mesmo servico pode entrar duas vezes na comanda. */
  key: string;
  discountPercent: number;
  professionalId: string | null;
  professionalName: string | null;
  professionalImage?: string | null;
  startAt: Date | null;
}

/** Grade de horarios da agenda - a sugestao cai sempre em um horario ofertado. */
const SLOT_MINUTES = 15;

const ceilToSlot = (date: Date) =>
  new Date(Math.ceil(date.getTime() / (SLOT_MINUTES * 60_000)) * (SLOT_MINUTES * 60_000));

const round = (value: number) => Math.round(value * 100) / 100;

interface UseAppointmentWizardParams {
  id?: string;
  unitId?: string;
  /** Nome da unidade quando nao ha sessao para consultar (pagina publica). */
  unitName?: string;
  /** Horario clicado na agenda: o primeiro servico ja nasce sugerido nele. */
  startAt?: Date;
  /**
   * Passo em que a modal abre. `professional` e o reagendamento: quem clicou em
   * "Reagendar" quer o calendario, nao o resumo do que ja esta marcado.
   */
  initialStep?: WizardStep;
  onDone: () => void;
}

/**
 * Estado do wizard de agendamento. Fica fora da tela para que o rodape da
 * modal - que so aceita acoes como dado - saiba em que passo esta e o que o
 * botao principal faz.
 */
export function useAppointmentWizard({
  id,
  unitId,
  unitName: givenUnitName,
  startAt: requestedStart,
  initialStep,
  onDone,
}: UseAppointmentWizardParams) {
  const mode = useBookingMode();
  const isEdit = Boolean(id);
  const { user } = useSession();
  const units = user?.units ?? [];

  const existing = useAppointment(mode ? undefined : id);
  const appointment = existing.data;

  // Pelo link do WhatsApp o agendamento chega pela rota publica, com o nome do
  // cliente mascarado - o suficiente para continuar de onde parou.
  const publicExisting = useQuery({
    queryKey: ['public-booking', mode?.slug ?? '', id ?? ''],
    queryFn: () => landingService.getBooking(mode?.slug as string, id as string),
    enabled: Boolean(mode && id),
  });

  const createAppointment = useCreateAppointment();
  const updateAppointment = useUpdateAppointment();
  /** O aviso ao cliente e acao propria: nao sai junto com o autosave. */
  const avisarCliente = useAppointmentAction();

  /** Id do que ja foi gravado nesta sessao - o autosave passa a atualizar. */
  const [savedId, setSavedId] = useState<string | null>(id ?? null);
  /** Fim do fluxo publico: a tela de confirmacao no lugar do wizard. */
  const [isConfirmed, setConfirmed] = useState(false);
  /** Autorizacao para alterar o horario que acabou de ser marcado aqui. */
  const [bookingToken, setBookingToken] = useState<string | null>(null);

  // Na pagina publica o agendamento vai pelas rotas sem sessao, com o token do
  // WhatsApp no lugar da credencial.
  const publicBooking = useMutation({
    mutationFn: async (input: {
      token: string;
      unitId: string;
      notes: string;
      items: { serviceId: string; professionalId?: string; startAt: string }[];
      appointmentId: string | null;
    }) => {
      const slug = mode?.slug as string;

      return input.appointmentId
        ? landingService.rebook(input.appointmentId, { slug, ...input })
        : landingService.book({ slug, ...input });
    },
    onSuccess: (result) => {
      setSavedId(result.id);
      // O token do telefone so marca; para mexer neste horario depois vale o
      // que a API devolve aqui, que e dele e de mais nenhum.
      if (result.token) setBookingToken(result.token);
    },
  });

  // Concluido, cancelado ou em andamento so pode ser consultado - a API
  // recusa a edicao, entao a tela mostra apenas o resumo.
  const editable =
    !appointment || appointment.status === 'SCHEDULED' || appointment.status === 'CONFIRMED';

  const [step, setStep] = useState<WizardStep>(isEdit ? 'summary' : 'customer');
  const [customer, setCustomer] = useState<WizardCustomer | null>(null);
  const [rows, setRows] = useState<WizardRow[]>([]);
  const [notes, setNotes] = useState('');
  /**
   * Cashback ja gravado no atendimento. Vale para o que nao se edita mais: com
   * o atendimento fechado o credito ja foi consumido e nao aparece no saldo.
   */
  const [cashbackSaved, setCashbackSaved] = useState(0);
  /** Cupom aplicado; o desconto vem calculado pela API. */
  const [coupon, setCoupon] = useState<{ id: string; code: string; discount: number } | null>(null);
  const [error, setError] = useState<string>();

  // Saldo do cliente. Pela pagina publica nao se usa cashback: quem abate e o
  // salao, no balcao.
  const cashbackSummary = useCashbackCustomer(mode === null ? customer?.id : undefined);
  const cashbackBalance = cashbackSummary.data?.active ?? 0;

  /** Servico escolhido esperando profissional e horario. */
  const [pending, setPending] = useState<WizardService | null>(null);
  /** Linha da comanda em edicao, quando o passo veio do resumo. */
  const [editingKey, setEditingKey] = useState<string | null>(null);
  /**
   * Posicao da linha em edicao. O salvamento automatico regrava os itens do
   * agendamento e eles voltam com outro id: sem a posicao, a edicao perderia
   * a linha no meio do caminho e o passo de horario ficaria sem servico.
   */
  const editingIndex = useRef(0);
  const [choice, setChoice] = useState<SlotChoice | null>(null);

  /**
   * Pelo link publico o cliente ve o que ja foi lancado, mas so mexe depois de
   * confirmar o WhatsApp - e o token que autoriza a alteracao.
   */
  useEffect(() => {
    const booking = publicExisting.data;
    if (!booking) return;

    setCustomer((current) => current ?? { id: '', name: booking.maskedCustomerName, phone: null });
    setNotes(booking.notes ?? '');
    setRows(
      booking.items.map((item) => ({
        key: `${item.serviceId}-${item.startAt}`,
        serviceId: item.serviceId,
        name: item.serviceName,
        price: item.price,
        durationMinutes: item.durationMinutes,
        maxDiscountPercent: 100,
        discountPercent: 0,
        professionalId: item.professionalId,
        professionalName: item.professionalName,
        startAt: new Date(item.startAt),
      })),
    );
    setStep('summary');
  }, [publicExisting.data]);

  // Ao abrir um agendamento existente, o wizard nasce preenchido.
  useEffect(() => {
    if (!appointment) return;

    setCustomer({
      id: appointment.customerId,
      name: appointment.customerName,
      phone: appointment.customerPhone,
      image: appointment.customerImage,
    });
    setNotes(appointment.notes ?? '');
    setCashbackSaved(appointment.cashbackUsed);
    setCoupon(
      appointment.couponId && appointment.couponCode
        ? {
            id: appointment.couponId,
            code: appointment.couponCode,
            discount: appointment.couponDiscount,
          }
        : null,
    );
    setRows(
      appointment.items.map((item) => ({
        key: item.id,
        serviceId: item.serviceId,
        name: item.serviceName,
        image: item.serviceImage,
        price: item.price,
        durationMinutes: item.durationMinutes,
        maxDiscountPercent: 100,
        discountPercent: item.discountPercent,
        professionalId: item.professionalId,
        professionalName: item.professionalName,
        professionalImage: item.professionalImage,
        startAt: new Date(item.startAt),
      })),
    );
  }, [appointment]);

  /** Sem unidade escolhida, o padrao e a matriz - nao a primeira da lista. */
  const defaultUnit = units.find((unit) => unit.type === 'HEAD_OFFICE') ?? units[0];
  /** Trocada dentro da modal; enquanto nao trocam, vale a da agenda. */
  const [chosenUnitId, setChosenUnitId] = useState<string | null>(null);

  const effectiveUnitId =
    appointment?.unitId ??
    publicExisting.data?.unitId ??
    chosenUnitId ??
    unitId ??
    defaultUnit?.id ??
    '';
  const unitName =
    appointment?.unitName ??
    publicExisting.data?.unitName ??
    units.find((unit) => unit.id === effectiveUnitId)?.name ??
    givenUnitName ??
    defaultUnit?.name ??
    '';

  /**
   * A comanda emenda: o proximo servico comeca quando o ultimo termina. No
   * primeiro, vale o horario clicado na agenda - ou agora.
   */
  const suggestedStart = useMemo(() => {
    const ends = rows
      .filter((row) => row.startAt !== null)
      .map((row) => (row.startAt as Date).getTime() + row.durationMinutes * 60_000);

    if (ends.length > 0) return ceilToSlot(new Date(Math.max(...ends)));

    return ceilToSlot(requestedStart ?? new Date());
  }, [rows, requestedStart]);

  const editingRow =
    rows.find((row) => row.key === editingKey) ??
    // Chaves trocadas pelo salvamento automatico: segue a mesma posicao.
    (editingKey ? (rows[editingIndex.current] ?? null) : null);
  const current = pending ?? editingRow;

  /** Os outros servicos da comanda tambem ocupam o profissional. */
  const busy = rows
    .filter((row) => row.key !== editingKey && row.startAt !== null)
    .map((row) => ({
      professionalId: row.professionalId,
      startAt: row.startAt as Date,
      durationMinutes: row.durationMinutes,
    }));

  const subtotal = round(rows.reduce((total, row) => total + row.price, 0));
  const afterDiscount = round(
    rows.reduce((total, row) => total + row.price * (1 - row.discountPercent / 100), 0),
  );
  // Cupom primeiro, cashback depois: o saldo do cliente abate o que sobrou.
  const couponDiscount = round(Math.min(coupon?.discount ?? 0, afterDiscount));
  const afterCoupon = round(afterDiscount - couponDiscount);
  // O saldo abate sozinho: ninguem escolhe quanto usar, o cashback entra como
  // desconto ate o limite do que sobrou da compra.
  const cashbackUsed = editable
    ? round(Math.min(cashbackBalance, afterCoupon))
    : round(Math.min(cashbackSaved, afterCoupon));
  const finalTotal = round(afterCoupon - cashbackUsed);
  const totalMinutes = rows.reduce((total, row) => total + row.durationMinutes, 0);
  const startAt = rows.some((row) => row.startAt)
    ? new Date(
        Math.min(
          ...rows.filter((row) => row.startAt).map((row) => (row.startAt as Date).getTime()),
        ),
      )
    : null;
  const endAt = rows.some((row) => row.startAt)
    ? new Date(
        Math.max(
          ...rows
            .filter((row) => row.startAt)
            .map((row) => (row.startAt as Date).getTime() + row.durationMinutes * 60_000),
        ),
      )
    : null;

  const chooseCustomer = useCallback((chosen: WizardCustomer) => {
    setCustomer(chosen);
    setError(undefined);
    setStep('service');
  }, []);

  /** Nome ou telefone mudaram no cadastro completo, sem trocar de cliente. */
  const updateCustomer = useCallback((changed: WizardCustomer) => {
    setCustomer((current) => ({ ...changed, token: current?.token }));
  }, []);

  const chooseService = useCallback((service: WizardService) => {
    setPending(service);
    setEditingKey(null);
    setChoice(null);
    setError(undefined);
    setStep('professional');
  }, []);

  const editRow = useCallback((row: WizardRow) => {
    setPending(null);
    setRows((current) => {
      editingIndex.current = Math.max(
        0,
        current.findIndex((item) => item.key === row.key),
      );

      return current;
    });
    setEditingKey(row.key);
    setChoice(
      row.professionalId && row.professionalName && row.startAt
        ? {
            professionalId: row.professionalId,
            professionalName: row.professionalName,
            professionalImage: row.professionalImage ?? null,
            startAt: row.startAt,
          }
        : null,
    );
    setStep('professional');
  }, []);

  // Reagendamento: o calendario abre no primeiro servico da comanda, ja com o
  // profissional e o horario atuais marcados. Guarda o id tratado, nao um
  // "ja abriu": a primeira renderizacao pode vir do agendamento anterior que
  // ainda esta no cache, e quando o certo chega o passo precisa reabrir.
  const abertoParaId = useRef<string | null>(null);
  useEffect(() => {
    if (initialStep !== 'professional' || !appointment) return;
    if (abertoParaId.current === appointment.id) return;

    const primeira = rows.find((row) => row.key === appointment.items[0]?.id);
    if (!primeira) return;

    abertoParaId.current = appointment.id;
    editRow(primeira);
  }, [appointment, rows, initialStep, editRow]);

  const removeRow = useCallback((key: string) => {
    setRows((current) => current.filter((row) => row.key !== key));
  }, []);

  const setDiscount = useCallback((key: string, discountPercent: number) => {
    const clamped = Math.min(100, Math.max(0, Math.round(discountPercent)));

    setRows((current) =>
      current.map((row) => (row.key === key ? { ...row, discountPercent: clamped } : row)),
    );
  }, []);

  /** Desconto do rodape: espalha o mesmo percentual por todas as linhas. */
  const applyUniformDiscount = useCallback((percent: number) => {
    const clamped = Math.min(100, Math.max(0, Math.round(percent)));

    setRows((current) => current.map((row) => ({ ...row, discountPercent: clamped })));
  }, []);

  /** Fecha o passo do profissional: cria a linha nova ou atualiza a editada. */
  const confirmSlot = useCallback(() => {
    if (!choice) return;

    if (editingKey) {
      setRows((current) =>
        current.map((row) =>
          row.key === editingKey
            ? {
                ...row,
                professionalId: choice.professionalId,
                professionalName: choice.professionalName,
                professionalImage: choice.professionalImage,
                startAt: choice.startAt,
              }
            : row,
        ),
      );
    } else if (pending) {
      setRows((current) => [
        ...current,
        {
          ...pending,
          key: crypto.randomUUID(),
          discountPercent: 0,
          professionalId: choice.professionalId,
          professionalName: choice.professionalName,
          professionalImage: choice.professionalImage,
          startAt: choice.startAt,
        },
      ]);
    }

    setPending(null);
    setEditingKey(null);
    setChoice(null);
    setStep('summary');
  }, [choice, editingKey, pending]);

  const goTo = useCallback(
    (next: WizardStep) => {
      // O erro do salvamento e daquele momento: mudar de passo o limpa.
      setError(undefined);
      createAppointment.reset();
      updateAppointment.reset();
      setStep(next);
    },
    [createAppointment, updateAppointment],
  );

  /** Tem tudo que a API exige: cliente, servicos, profissional e horario. */
  const isComplete =
    customer !== null &&
    rows.length > 0 &&
    rows.every((row) => row.startAt !== null && row.professionalId !== null);

  const persist = useCallback(
    (onSaved?: () => void) => {
      if (!customer || !isComplete) return;

      const items = rows.map((row) => ({
        serviceId: row.serviceId,
        discountPercent: row.discountPercent,
        professionalId: row.professionalId ?? undefined,
        startAt: row.startAt as Date,
      }));

      if (mode) {
        if (!customer.token) {
          setError('Confirme seu WhatsApp para continuar');
          setStep('customer');
          return;
        }

        // Horario marcado em outro momento (ou em outra aba): a autorizacao
        // para altera-lo vive na resposta da marcacao e nao chegou aqui.
        if (savedId && !bookingToken) {
          setError('Para mudar este horario, fale com o salao');
          return;
        }

        publicBooking.mutate(
          {
            token: savedId && bookingToken ? bookingToken : customer.token,
            unitId: effectiveUnitId,
            notes,
            appointmentId: savedId,
            items: items.map((item) => ({
              serviceId: item.serviceId,
              professionalId: item.professionalId,
              startAt: (item.startAt as Date).toISOString(),
            })),
          },
          { onSuccess: () => onSaved?.() },
        );
        return;
      }

      const input = {
        customerId: customer.id,
        unitId: effectiveUnitId,
        notes,
        startAt: startAt as Date,
        // Sem profissional escolhido, quem aloca e o sistema.
        anyProfessional: items.every((item) => !item.professionalId),
        items,
        cashbackUsed,
        couponId: coupon?.id,
        couponCode: coupon?.code,
        couponDiscount,
      };

      if (savedId) {
        updateAppointment.mutate({ id: savedId, input }, { onSuccess: () => onSaved?.() });
        return;
      }

      createAppointment.mutate(input, {
        onSuccess: (created) => {
          setSavedId(created.id);
          onSaved?.();
        },
      });
    },
    [
      customer,
      isComplete,
      rows,
      notes,
      cashbackUsed,
      coupon,
      couponDiscount,
      startAt,
      effectiveUnitId,
      savedId,
      bookingToken,
      mode,
      publicBooking,
      createAppointment,
      updateAppointment,
    ],
  );

  /**
   * Autosave: o agendamento se grava sozinho assim que fica completo e a cada
   * mudanca depois disso. A impressao digital e o conteudo, nao a identidade
   * das linhas - assim recarregar o agendamento salvo nao dispara outro save.
   */
  const fingerprint = JSON.stringify({
    customerId: customer?.id ?? '',
    unitId: effectiveUnitId,
    notes,
    cashbackUsed,
    items: rows.map((row) => ({
      serviceId: row.serviceId,
      professionalId: row.professionalId,
      startAt: row.startAt?.toISOString() ?? null,
      discountPercent: row.discountPercent,
    })),
  });
  const lastSaved = useRef<string | null>(null);

  useEffect(() => {
    if (!editable || !isComplete) return;
    // A primeira leitura de um agendamento existente nao e uma alteracao.
    if (lastSaved.current === null && isEdit) {
      lastSaved.current = fingerprint;
      return;
    }
    if (lastSaved.current === fingerprint) return;

    const timer = setTimeout(() => {
      lastSaved.current = fingerprint;
      persist();
    }, 800);

    return () => clearTimeout(timer);
  }, [editable, isComplete, isEdit, fingerprint, persist]);

  const submit = useCallback(() => {
    if (!customer) {
      setError('Escolha o cliente');
      setStep('customer');
      return;
    }

    if (rows.length === 0) {
      setError('Lance ao menos um servico');
      setStep('service');
      return;
    }

    if (!isComplete) {
      setError('Defina o profissional e o horario de cada servico');
      return;
    }

    // A mensagem para o cliente sai so aqui, no fim: ate este clique a
    // comanda ainda pode ganhar servico, e avisar antes seria mandar um
    // horario que ainda vai mudar.
    persist(() => {
      const id = savedId;
      if (id && !mode) avisarCliente.mutate({ id, action: 'notify' });

      // Na pagina publica o fim do fluxo e uma confirmacao na tela, nao o
      // fechamento: quem agendou sozinho precisa ver que deu certo.
      if (mode) setConfirmed(true);
      else onDone();
    });
  }, [customer, rows, isComplete, persist, onDone, mode, savedId, avisarCliente]);

  /**
   * Resumo em uma frase, para mandar no WhatsApp: o que foi marcado, quando,
   * com quem e onde.
   */
  const summaryText = useMemo(() => {
    if (!startAt) return '';

    const dia = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'long' }).format(startAt);
    const hora = new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(startAt);
    const servicos = rows.map((row) => row.name).join(', ');
    const profissionais = [
      ...new Set(rows.map((row) => row.professionalName).filter(Boolean)),
    ].join(', ');

    return `Ola! Foi realizado um agendamento de ${servicos} as ${hora} de ${dia}${
      profissionais ? `, com ${profissionais}` : ''
    }${unitName ? ` na ${unitName}` : ''}.`;
  }, [rows, startAt, unitName]);

  return {
    step,
    goTo,
    isEdit,
    editable,
    appointment,
    isLoading: isEdit && existing.isLoading,
    loadError: isEdit && existing.isError ? existing.error : null,
    mutationError: createAppointment.error ?? updateAppointment.error ?? publicBooking.error,
    isSubmitting:
      createAppointment.isPending || updateAppointment.isPending || publicBooking.isPending,
    /** Ja gravado: o rodape troca "Agendar" por "Concluir" e o link do zap aparece. */
    savedId,
    isPublic: mode !== null,
    isConfirmed,
    summaryText,
    isLoadingBooking: publicExisting.isLoading,
    error,
    setError,

    unitId: effectiveUnitId,
    unitName,
    units,
    /** Trocar a unidade so faz sentido antes de o agendamento existir. */
    canChangeUnit: !appointment && !publicExisting.data && units.length > 1,
    chooseUnit: setChosenUnitId,
    customer,
    chooseCustomer,
    updateCustomer,

    rows,
    removeRow,
    setDiscount,
    applyUniformDiscount,
    editRow,
    chooseService,
    startAddService: () => goTo('service'),

    current,
    choice,
    setChoice,
    confirmSlot,
    suggestedStart,
    busy,

    notes,
    setNotes,

    subtotal,
    discountTotal: round(subtotal - afterDiscount),
    /** Saldo que o cliente tem a usar hoje. */
    cashbackBalance,
    cashbackUsed,
    coupon,
    couponDiscount,
    /** A tela manda o cupom ja validado pela API, ou null para tirar. */
    setCoupon,
    finalTotal,
    totalMinutes,
    startAt,
    endAt,

    submit,
  };
}

export type AppointmentWizard = ReturnType<typeof useAppointmentWizard>;
